import * as React from "react";
import { useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { accounts } from "@/api/accounts";
import { useSession } from "@/auth/session";

const RECENT_KEY = "switchboard-recent-accounts";

/** The account the console is acting on, taken from the URL (`/a/:accountId/...`). */
export function useActingAccountId(): string | undefined {
  const { accountId } = useParams<{ accountId: string }>();
  const own = useSession((s) => s.session?.accountId);
  if (!accountId || accountId === "_") return own;
  return accountId;
}

export function useAccount(id: string | undefined) {
  return useQuery({
    queryKey: ["account", id],
    queryFn: () => accounts.get(id!),
    enabled: Boolean(id),
    staleTime: 60_000,
  });
}

export interface RecentAccount {
  id: string;
  name: string;
}

export function readRecentAccounts(): RecentAccount[] {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) ?? "[]") as RecentAccount[];
  } catch {
    return [];
  }
}

export function rememberAccount(a: RecentAccount) {
  try {
    const next = [a, ...readRecentAccounts().filter((r) => r.id !== a.id)].slice(0, 8);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    /* private mode */
  }
}

/** Drop an account from the recent list: a deleted one answers 401, which reads as a dead token. */
export function forgetRecentAccount(id: string) {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(readRecentAccounts().filter((r) => r.id !== id)));
  } catch {
    /* private mode */
  }
}

/** Keep the recent list current while the user works inside an account. */
export function useRememberAccount(id: string | undefined, name: string | undefined) {
  React.useEffect(() => {
    if (id && name) rememberAccount({ id, name });
  }, [id, name]);
}
